import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { User } from '../types';
import { storageService } from '../services/storage.service';
import { useApi } from '../api/hooks/useApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { request } = useApi();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Recupera sessão ao iniciar app
  useEffect(() => {
  const savedToken = storageService.getAccessToken();

  if (savedToken) {
    refreshToken()
      .catch(() => {
        logout();
      })
      .finally(() => {
        setIsLoading(false);
      });
  } else {
    setIsLoading(false);
  }
}, []);

  const login = async (email: string, password: string) => {
    const data = await request<{
      response: {
        token: string;
        user: User;
      };
    }>(`api/auth/signin`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    const { token, user } = data.response;

    setUser(user);
    storageService.setTokens(token, "");
    localStorage.setItem('user', JSON.stringify(user));
    await refreshToken();
  };


  const logout = () => {
    setUser(null);
    storageService.clearTokens();
    localStorage.removeItem('user');
  };

  const refreshToken = async () => {
    try {
      const data = await request<{
        response: {
          user: User;
          token: string;
        };
      }>(`api/auth/token`, {
        method: "GET",
      });
      setUser(data.response.user);
      storageService.setTokens(data.response.token, "");
      localStorage.setItem("user", JSON.stringify(data.response.user));

      return data.response.user;
    } catch (err: any) {
      if (err.message?.includes("401")) {
        logout();
      }
      throw err;
    }
  };


  const value: AuthContextType = {
    user,
    setUser,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
